/**
 * Push notification registration. Requests permission, listens for the
 * APNs/FCM token, and POSTs it to /v1/me/devices so the backend can
 * target this device for behavior-driven pushes.
 *
 * No-op on web — `@capacitor/push-notifications` only works on native.
 */

import { PushNotifications } from '@capacitor/push-notifications';
import { api } from '@/api/client';
import { logger } from './logger';
import { platform } from './platform';

interface RegisterDeviceBody {
  platform: 'ios' | 'android';
  pushToken: string;
  appVersion?: string;
}

interface DeviceResponse {
  id: string;
  userId: string;
  platform: string;
  appVersion: string | null;
}

const APP_VERSION = (import.meta.env.VITE_APP_VERSION as string | undefined) ?? '0.1.0';

async function postDevice(body: RegisterDeviceBody): Promise<void> {
  try {
    await api.post<DeviceResponse>('/v1/me/devices', body);
  } catch (err) {
    // Push registration is non-critical for app function; log and move on.
    logger.warn('register device failed', { err: String(err) });
  }
}

export type PushPermissionState = 'granted' | 'denied' | 'prompt';

/**
 * Inspect the OS-level push permission without prompting. Safe on every
 * platform — `checkPermissions` does not touch Firebase. On web returns
 * 'prompt' (no OS concept of standing permission).
 */
export async function checkPushPermission(): Promise<PushPermissionState> {
  if (!platform.isNative) return 'prompt';
  try {
    const perm = await PushNotifications.checkPermissions();
    if (perm.receive === 'granted') return 'granted';
    if (perm.receive === 'denied') return 'denied';
    return 'prompt';
  } catch (err) {
    logger.warn('push checkPermissions failed', { err: String(err) });
    return 'denied';
  }
}

/**
 * Trigger the OS permission prompt. Safe on every platform — the
 * Firebase-dependent path is `register()`, not the permission flow.
 * Returns the resulting state. If the OS suppresses the prompt
 * (Android 13+ permanent deny), returns 'denied' immediately.
 */
export async function requestPushPermission(): Promise<PushPermissionState> {
  if (!platform.isNative) return 'prompt';
  try {
    const requested = await PushNotifications.requestPermissions();
    if (requested.receive === 'granted') return 'granted';
    if (requested.receive === 'denied') return 'denied';
    return 'prompt';
  } catch (err) {
    logger.warn('push requestPermissions failed', { err: String(err) });
    return 'denied';
  }
}

/**
 * Idempotent. Safe to call on every cold launch — the backend upserts
 * by push_token. Returns when registration listeners are wired (the
 * native callback fires asynchronously, possibly seconds later).
 */
export async function registerForPush(): Promise<void> {
  if (!platform.isNative) return;

  const native: 'ios' | 'android' = platform.isIOS ? 'ios' : 'android';

  // Android push requires Firebase Cloud Messaging. Without
  // google-services.json + the google-services Gradle plugin, the native
  // PushNotifications.register() throws IllegalStateException synchronously
  // on a Handler thread, which escapes Capacitor's bridge catch and crashes
  // the process. Until FCM is wired up, also skip the launch-time prompt on
  // Android — granting permission would be functionally meaningless. The
  // FollowScreen toggle still invokes requestPushPermission() in-context,
  // so users opt in there once Android push actually works end-to-end.
  if (native === 'android') return;

  // Permission gate — iOS shows a prompt; Android 13+ also prompts (when
  // we eventually run this on Android).
  const status = await checkPushPermission();
  let granted = status === 'granted';
  if (!granted) {
    const requested = await requestPushPermission();
    granted = requested === 'granted';
  }
  if (!granted) {
    logger.info('push permission denied');
    return;
  }

  // Listeners must be in place BEFORE register() — the platform may
  // fire `registration` before the call resolves.
  const registered = await PushNotifications.addListener('registration', (token) => {
    void postDevice({
      platform: native,
      pushToken: token.value,
      appVersion: APP_VERSION,
    });
  });
  const registrationError = await PushNotifications.addListener(
    'registrationError',
    (err) => {
      logger.warn('push registration error', { err: String(err.error) });
    },
  );

  try {
    await PushNotifications.register();
  } catch (err) {
    logger.warn('push register call failed', { err: String(err) });
    void registered.remove();
    void registrationError.remove();
  }
  // We intentionally don't remove the listeners on success — APNs/FCM
  // can re-issue tokens at any time, and we want every refresh to flow
  // through `postDevice`. They live for the app's lifetime.
}
